import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  Cpu, Key, Eye, EyeOff, CheckCircle, XCircle, 
  AlertTriangle, BarChart3, DollarSign, Zap, 
  RefreshCw, Settings, Info, Shield, TrendingUp,
  Activity, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const AI_PROVIDERS = [
  { id: 'openai', name: 'OpenAI', description: 'GPT-4o, GPT-4o-mini, GPT-3.5' },
  { id: 'anthropic', name: 'Anthropic', description: 'Claude 3 Opus, Sonnet, Haiku' },
  { id: 'google', name: 'Google', description: 'Gemini 1.5 Pro, Flash' }
];

const AI_FEATURES = [
  { id: 'chatbot', name: 'AI Chatbot', description: 'Internal HR assistant for employees' },
  { id: 'document_summary', name: 'Document Summary', description: 'Auto-summarize uploaded documents' },
  { id: 'performance_insights', name: 'Performance Insights', description: 'AI-powered performance analysis' },
  { id: 'leave_predictions', name: 'Leave Predictions', description: 'Predict leave patterns' }
];

export default function AIConfiguration() {
  const [config, setConfig] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [keyValidationResult, setKeyValidationResult] = useState(null);

  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isOwner = user.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, usageRes] = await Promise.all([
        axios.get(`${API}/ai/config`),
        axios.get(`${API}/ai/usage`)
      ]);
      setConfig(configRes.data);
      setUsage(usageRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only Company Owner can access AI configuration');
      } else {
        console.error('Failed to fetch AI config:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAI = async () => {
    if (!isOwner) {
      toast.error('Only Company Owner can modify AI settings');
      return;
    }

    // Check if at least one API key is configured before enabling
    if (!config?.ai_enabled) {
      const hasKey = config?.openai_api_key_masked || 
                     config?.anthropic_api_key_masked || 
                     config?.google_api_key_masked;
      if (!hasKey) {
        toast.error('Please configure at least one AI provider API key first');
        return;
      }
    }

    setSaving(true);
    try {
      await axios.put(`${API}/ai/config`, {
        ai_enabled: !config.ai_enabled
      });
      toast.success(`AI features ${config.ai_enabled ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update AI settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectProvider = async (providerId) => {
    if (!isOwner) {
      toast.error('Only Company Owner can modify AI settings');
      return;
    }

    // Check if provider has key configured
    const keyField = `${providerId}_api_key_masked`;
    if (!config?.[keyField]) {
      toast.error(`Please configure ${providerId.toUpperCase()} API key first`);
      setSelectedProvider(providerId);
      setShowKeyDialog(true);
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/ai/config`, {
        active_provider: providerId
      });
      toast.success(`${providerId.toUpperCase()} selected as active provider`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update provider');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectModel = async (model) => {
    if (!isOwner) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/ai/config`, {
        active_model: model
      });
      toast.success(`Model updated to ${model}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update model');
    } finally {
      setSaving(false);
    }
  };

  const handleValidateKey = async () => {
    if (!newApiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    setValidating(true);
    setKeyValidationResult(null);
    
    try {
      const response = await axios.post(`${API}/ai/validate-key`, {
        provider: selectedProvider,
        api_key: newApiKey
      });
      setKeyValidationResult(response.data);
    } catch (error) {
      setKeyValidationResult({
        valid: false,
        message: error.response?.data?.detail || 'Validation failed'
      });
    } finally {
      setValidating(false);
    }
  };

  const handleSaveKey = async () => {
    if (!keyValidationResult?.valid) {
      toast.error('Please validate the API key first');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/ai/config`, {
        [`${selectedProvider}_api_key`]: newApiKey
      });
      toast.success(`${selectedProvider.toUpperCase()} API key saved`);
      setShowKeyDialog(false);
      setNewApiKey('');
      setKeyValidationResult(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLimits = async (field, value) => {
    if (!isOwner) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/ai/config`, {
        [field]: value
      });
      toast.success('Limits updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update limits');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFeature = async (featureId) => {
    if (!isOwner) return;
    
    const currentFeatures = config?.enabled_features || [];
    const newFeatures = currentFeatures.includes(featureId)
      ? currentFeatures.filter(f => f !== featureId)
      : [...currentFeatures, featureId];
    
    setSaving(true);
    try {
      await axios.put(`${API}/ai/config`, {
        enabled_features: newFeatures
      });
      toast.success('Feature settings updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update features');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveKey = async (providerId) => {
    if (!isOwner) return;
    
    if (!confirm(`Are you sure you want to remove the ${providerId.toUpperCase()} API key?`)) {
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/ai/config`, {
        [`${providerId}_api_key`]: null
      });
      toast.success(`${providerId.toUpperCase()} API key removed`);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove API key');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-xl p-6">
        <div className="flex items-center space-x-3">
          <Shield className="h-6 w-6 text-yellow-600" />
          <div>
            <h3 className="font-semibold text-yellow-800 dark:text-yellow-200">Access Restricted</h3>
            <p className="text-yellow-700 dark:text-yellow-300 text-sm">
              Only the Company Owner can configure AI settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const activeProvider = AI_PROVIDERS.find(p => p.id === config?.active_provider);
  const availableModels = config?.available_models?.[config?.active_provider] || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Cpu className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">AI Configuration</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Configure and manage AI features using your own API keys
            </p>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800 dark:text-blue-200 text-sm">Your API Keys, Your Control</h3>
            <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
              AI features require you to provide your own API keys. All AI costs are billed directly to your accounts 
              with OpenAI, Anthropic, or Google. MyHR Orbit does not store or have access to your API traffic.
            </p>
          </div>
        </div>
      </div>

      {/* Master Toggle */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
              config?.ai_enabled 
                ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                : 'bg-slate-100 dark:bg-slate-700'
            }`}>
              <Zap className={`h-5 w-5 ${config?.ai_enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">AI Features</h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                {config?.ai_enabled ? 'AI features are active' : 'AI features are disabled'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`text-sm font-medium ${config?.ai_enabled ? 'text-emerald-600' : 'text-slate-500'}`}>
              {config?.ai_enabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch
              checked={config?.ai_enabled || false}
              onCheckedChange={handleToggleAI}
              disabled={saving}
              data-testid="ai-master-toggle"
            />
          </div>
        </div>
      </div>

      {/* Provider Selection */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
          <Settings className="h-5 w-5 mr-2" />
          AI Provider
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          {AI_PROVIDERS.map((provider) => {
            const keyMasked = config?.[`${provider.id}_api_key_masked`];
            const isActive = config?.active_provider === provider.id;
            
            return (
              <div
                key={provider.id}
                className={`relative rounded-xl border-2 p-4 cursor-pointer transition-all ${
                  isActive
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                }`}
                onClick={() => handleSelectProvider(provider.id)}
                data-testid={`provider-${provider.id}`}
              >
                {isActive && (
                  <div className="absolute -top-2 -right-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-slate-900 dark:text-white">{provider.name}</h4>
                  {keyMasked ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                      Key Set
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500">
                      No Key
                    </span>
                  )}
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-sm mb-3">{provider.description}</p>
                {keyMasked && (
                  <div className="flex items-center justify-between text-xs">
                    <code className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-300">
                      {keyMasked}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveKey(provider.id);
                      }}
                      className="text-red-500 hover:text-red-600 text-xs h-6 px-2"
                      data-testid={`remove-key-${provider.id}`}
                    >
                      Remove
                    </Button>
                  </div>
                )}
                {!keyMasked && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProvider(provider.id);
                      setShowKeyDialog(true);
                    }}
                    className="w-full mt-2"
                    data-testid={`add-key-${provider.id}`}
                  >
                    <Key className="h-3 w-3 mr-1" />
                    Add API Key
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Model Selection (only if provider is selected) */}
      {config?.active_provider && config?.active_provider !== 'none' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4">
            Select Model ({activeProvider?.name})
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(availableModels).map(([modelId, pricing]) => (
              <div
                key={modelId}
                className={`rounded-lg border p-3 cursor-pointer transition-all ${
                  config?.active_model === modelId
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-slate-200 dark:border-slate-600 hover:border-slate-300'
                }`}
                onClick={() => handleSelectModel(modelId)}
                data-testid={`model-${modelId}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-slate-900 dark:text-white text-sm">{modelId}</span>
                  {config?.active_model === modelId && (
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                  )}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  <span>In: ${pricing.input}/1K tokens</span>
                  <span className="mx-2">•</span>
                  <span>Out: ${pricing.output}/1K tokens</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usage & Limits */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Usage */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2" />
            Current Month Usage
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-600 dark:text-slate-400 text-sm">Total Requests</span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {usage?.usage?.total_requests || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600 dark:text-slate-400 text-sm">Input Tokens</span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {(usage?.usage?.input_tokens || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600 dark:text-slate-400 text-sm">Output Tokens</span>
              <span className="font-semibold text-slate-900 dark:text-white">
                {(usage?.usage?.output_tokens || 0).toLocaleString()}
              </span>
            </div>
            <div className="border-t border-slate-200 dark:border-slate-600 pt-3 flex items-center justify-between">
              <span className="text-slate-600 dark:text-slate-400 text-sm flex items-center">
                <DollarSign className="h-4 w-4 mr-1" />
                Estimated Cost
              </span>
              <span className="font-bold text-emerald-600 dark:text-emerald-400">
                ${(usage?.usage?.estimated_cost_usd || 0).toFixed(4)}
              </span>
            </div>
          </div>
        </div>

        {/* Limits Configuration */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            Usage Limits
          </h3>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-600 dark:text-slate-400 text-sm">Monthly Token Limit</Label>
              <div className="flex items-center space-x-2 mt-1">
                <Input
                  type="number"
                  value={config?.monthly_token_limit || ''}
                  onChange={(e) => setConfig({...config, monthly_token_limit: parseInt(e.target.value) || 0})}
                  placeholder="0 = unlimited"
                  className="flex-1"
                  data-testid="token-limit-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUpdateLimits('monthly_token_limit', config?.monthly_token_limit || 0)}
                  disabled={saving}
                  data-testid="save-token-limit"
                >
                  Save
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Set to 0 for unlimited</p>
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-400 text-sm">Monthly Cost Limit (USD)</Label>
              <div className="flex items-center space-x-2 mt-1">
                <Input
                  type="number"
                  step="0.01"
                  value={config?.monthly_cost_limit || ''}
                  onChange={(e) => setConfig({...config, monthly_cost_limit: parseFloat(e.target.value) || 0})}
                  placeholder="0 = unlimited"
                  className="flex-1"
                  data-testid="cost-limit-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUpdateLimits('monthly_cost_limit', config?.monthly_cost_limit || 0)}
                  disabled={saving}
                  data-testid="save-cost-limit"
                >
                  Save
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Set to 0 for unlimited</p>
            </div>
            
            {/* Limit Status */}
            {usage?.limit_status && (
              <div className="mt-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-400">Tokens Remaining</span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {usage.limit_status.tokens_remaining}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-slate-600 dark:text-slate-400">Cost Remaining</span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {typeof usage.limit_status.cost_remaining === 'number' 
                      ? `$${usage.limit_status.cost_remaining.toFixed(2)}`
                      : usage.limit_status.cost_remaining}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Features */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
          <Zap className="h-5 w-5 mr-2" />
          AI Features
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {AI_FEATURES.map((feature) => {
            const isEnabled = config?.enabled_features?.includes(feature.id);
            return (
              <div
                key={feature.id}
                className={`rounded-lg border p-4 ${
                  isEnabled 
                    ? 'border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20' 
                    : 'border-slate-200 dark:border-slate-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white">{feature.name}</h4>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">{feature.description}</p>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() => handleToggleFeature(feature.id)}
                    disabled={saving || !config?.ai_enabled}
                    data-testid={`feature-${feature.id}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
        {!config?.ai_enabled && (
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-4 flex items-center">
            <Info className="h-4 w-4 mr-2" />
            Enable AI features above to configure individual features
          </p>
        )}
      </div>

      {/* API Key Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Configure {selectedProvider?.toUpperCase()} API Key</DialogTitle>
            <DialogDescription>
              Enter your API key from {selectedProvider === 'openai' ? 'OpenAI' : 
                selectedProvider === 'anthropic' ? 'Anthropic' : 'Google Cloud'}.
              All AI costs will be billed to your account.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>API Key</Label>
              <div className="relative mt-1">
                <Input
                  type={showApiKey[selectedProvider] ? 'text' : 'password'}
                  value={newApiKey}
                  onChange={(e) => {
                    setNewApiKey(e.target.value);
                    setKeyValidationResult(null);
                  }}
                  placeholder={
                    selectedProvider === 'openai' ? 'sk-...' :
                    selectedProvider === 'anthropic' ? 'sk-ant-...' :
                    'AI...'
                  }
                  className="pr-10"
                  data-testid="api-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey({...showApiKey, [selectedProvider]: !showApiKey[selectedProvider]})}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showApiKey[selectedProvider] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Where to get key */}
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Get your API key from:{' '}
              <a
                href={
                  selectedProvider === 'openai' ? 'https://platform.openai.com/api-keys' :
                  selectedProvider === 'anthropic' ? 'https://console.anthropic.com/settings/keys' :
                  'https://console.cloud.google.com/apis/credentials'
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 hover:underline"
              >
                {selectedProvider === 'openai' ? 'OpenAI Platform' :
                 selectedProvider === 'anthropic' ? 'Anthropic Console' :
                 'Google Cloud Console'}
              </a>
            </div>

            {/* Validation Result */}
            {keyValidationResult && (
              <div className={`p-3 rounded-lg flex items-center space-x-2 ${
                keyValidationResult.valid 
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' 
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              }`}>
                {keyValidationResult.valid ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                <span className="text-sm">{keyValidationResult.message}</span>
              </div>
            )}

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleValidateKey}
                disabled={validating || !newApiKey.trim()}
                className="flex-1"
                data-testid="validate-key-btn"
              >
                {validating ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Test Connection
              </Button>
              <Button
                onClick={handleSaveKey}
                disabled={saving || !keyValidationResult?.valid}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-key-btn"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Key className="h-4 w-4 mr-2" />
                )}
                Save Key
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
